import React from "react";
import { OrderItem, OrderStatus } from "@/types/orders";
import { Badge } from "@/components/ui/badge";
import { formatCurrency } from "@/lib/currencyFormatter";
import { useTranslation } from "react-i18next";
import {
  QrCode,
  Check,
  Clock,
  Package,
  Utensils,
  Image,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useOrderStore } from "@/store/orderStore";
import { updateOrderStatus as updateOrderStatusService } from '@/services/orders';
import { useIsMobile } from "@/hooks/use-mobile";

interface OrderItemsManagementProps {
  items: OrderItem[];
  orderId?: string;
  tableNumber?: number;
  tableName?: string;
  status?: OrderStatus;
  specialInstructions?: string;
  viewOnly?: boolean; // Add viewOnly prop
}

export function OrderItemsManagement({
  items,
  orderId,
  tableNumber,
  tableName,
  status = "pending",
  specialInstructions,
  viewOnly = false, // Default to false for backwards compatibility
}: OrderItemsManagementProps) {
  const { t } = useTranslation();
  const { updateOrder: updateOrderInStore } = useOrderStore();
  const isMobile = useIsMobile();

  const totalQuantity = items.reduce((sum, item) => sum + item.quantity, 0);
  const totalPrice = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );

  const handleUpdateStatus = async (
    newStatus: OrderStatus,
    e: React.MouseEvent
  ) => {
    e.stopPropagation();

    if (!orderId || viewOnly) return;

    try {
      const result = await updateOrderStatusService(orderId, newStatus, status);
      if (result.success) {
        // Update the order in the store
        updateOrderInStore({ status: newStatus });
      } else {
        console.error("Error updating order status:", result.error);
      }
    } catch (error) {
      console.error("Error updating order status:", error);
    }
  };

  // Single function to render status badge to avoid duplication
  const renderStatusBadge = () => {
    // Only show status badge in header if not in viewOnly mode
    // since it's already displayed in the dialog title
    if (viewOnly) return null;

    switch (status) {
      case "pending":
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
            <Clock className="h-3 w-3 mr-1" />
            {t("pending")}
          </Badge>
        );
      case "preparing":
        return (
          <Badge variant="outline" className="bg-blue-100 text-blue-800">
            <Utensils className="h-3 w-3 mr-1" />
            {t("preparing")}
          </Badge>
        );
      case "ready":
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800">
            <Package className="h-3 w-3 mr-1" />
            {t("ready")}
          </Badge>
        );
      case "delivered":
        return (
          <Badge variant="outline" className="bg-purple-100 text-purple-800">
            <Check className="h-3 w-3 mr-1" />
            {t("delivered")}
          </Badge>
        );
      case "completed":
        return (
          <Badge variant="outline" className="bg-gray-100 text-gray-800">
            <Check className="h-3 w-3 mr-1" />
            {t("completed")}
          </Badge>
        );
      default:
        return null;
    }
  };

  // Helper function to determine next status
  const getNextStatus = (currentStatus: OrderStatus): OrderStatus => {
    switch (currentStatus) {
      case "pending":
        return "preparing";
      case "preparing":
        return "ready";
      case "ready":
        return "delivered";
      case "delivered":
        return "completed";
      default:
        return currentStatus;
    }
  };

  // Helper function to get button text and styles based on current status
  const getActionButton = (currentStatus: OrderStatus) => {
    // Don't show action buttons in viewOnly mode
    if (
      viewOnly ||
      currentStatus === "completed" ||
      currentStatus === "cancelled"
    ) {
      return null;
    }

    const nextStatus = getNextStatus(currentStatus);

    let buttonText = "";
    let buttonStyle = "";

    switch (nextStatus) {
      case "preparing":
        buttonText = isMobile ? t("preparing") : t("startPreparing");
        buttonStyle =
          "bg-blue-100 text-blue-800 hover:bg-blue-200 border-blue-300";
        break;
      case "ready":
        buttonText = isMobile ? t("ready") : t("markReady");
        buttonStyle =
          "bg-green-100 text-green-800 hover:bg-green-200 border-green-300";
        break;
      case "delivered":
        buttonText = isMobile ? t("delivered") : t("markDelivered");
        buttonStyle =
          "bg-purple-100 text-purple-800 hover:bg-purple-200 border-purple-300";
        break;
      case "completed":
        buttonText = isMobile ? t("completed") : t("markCompleted");
        buttonStyle =
          "bg-gray-100 text-gray-800 hover:bg-gray-200 border-gray-300";
        break;
      default:
        return null;
    }

    return (
      <Button
        size={isMobile ? "sm" : "default"}
        variant="outline"
        className={`${buttonStyle} text-xs sm:text-sm px-2 sm:px-4 h-7 sm:h-9`}
        onClick={(e) => handleUpdateStatus(nextStatus, e)}
      >
        {getButtonIcon(nextStatus)}
        {buttonText}
      </Button>
    );
  };

  // Helper function to get appropriate icon for the action button
  const getButtonIcon = (nextStatus: OrderStatus) => {
    switch (nextStatus) {
      case "preparing":
        return <Utensils className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />;
      case "ready":
        return <Package className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />;
      case "delivered":
        return <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />;
      case "completed":
        return <Check className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1" />;
      default:
        return null;
    }
  };

  return (
    <div className="rounded-lg border shadow-sm">
      {/* Header with table info and status action buttons */}
      <div className="flex flex-col p-2 sm:p-3 bg-gray-50 border-b rounded-t-lg">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-1 sm:gap-2">
            <QrCode className="h-3 w-3 sm:h-4 sm:w-4 text-gray-500" />
            <span className="font-medium text-xs sm:text-sm">
              {tableNumber && `${t("table")} ${tableNumber}`}
              {tableName && ` - ${tableName}`}
            </span>
          </div>
          <div className="flex flex-wrap items-center gap-1 sm:gap-2">
            {renderStatusBadge()}
            <Badge variant="outline" className="bg-gray-100 text-xs">
              {totalQuantity} {t("items")}
            </Badge>
            <Badge
              variant="outline"
              className="bg-gray-100 font-medium text-xs sm:text-sm"
            >
              {formatCurrency(totalPrice)}
            </Badge>
          </div>
        </div>

        {/* Special Instructions - Displayed directly in the header section */}
        {specialInstructions && (
          <div className="mt-1 sm:mt-2 bg-yellow-50 border border-yellow-200 p-1.5 sm:p-2 rounded-md">
            <div className="flex items-center gap-1 sm:gap-1.5 font-medium text-yellow-800 text-xs sm:text-sm mb-0.5 sm:mb-1">
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4" />
              {t("specialInstructions")}:
            </div>
            <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
              {specialInstructions}
            </p>
          </div>
        )}

        {/* Status action buttons - Only show if not in viewOnly mode */}
        {orderId &&
          status !== "completed" &&
          status !== "cancelled" &&
          !viewOnly && (
            <div className="flex flex-wrap gap-1 sm:gap-2 justify-end mt-1">
              {getActionButton(status)}
            </div>
          )}
      </div>

      {/* Order items list - all details visible by default for quick chef reference */}
      <div className="divide-y">
        {items.map((item) => (
          <div key={item.id} className="flex p-2 sm:p-3 gap-2 sm:gap-3">
            {/* Item image */}
            <div
              className={`${
                isMobile ? "h-12 w-12" : "h-16 w-16"
              } rounded-md overflow-hidden bg-gray-100 flex-shrink-0`}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.menu_item_name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center text-gray-400">
                  <Image className="h-4 w-4 sm:h-5 sm:w-5" />
                </div>
              )}
            </div>

            {/* Item details */}
            <div className="flex-1">
              <div className="flex items-start justify-between">
                <div>
                  <h4 className="font-medium text-sm sm:text-base">
                    {item.menu_item_name}
                  </h4>
                  <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                    <Badge variant="outline" className="bg-gray-100 text-xs">
                      {item.quantity}x
                    </Badge>
                    <span className="text-xs sm:text-sm text-gray-500">
                      {formatCurrency(item.price)}
                    </span>
                  </div>
                </div>
                <span className="font-medium text-sm sm:text-base">
                  {formatCurrency(item.price * item.quantity)}
                </span>
              </div>

              {/* Notes - always visible for kitchen staff */}
              {item.notes && (
                <div className="mt-1.5 sm:mt-2 bg-yellow-50 border border-yellow-200 p-1.5 sm:p-2 rounded-md">
                  <p className="text-xs font-medium text-yellow-700">
                    {t("notes")}:
                  </p>
                  <p className="text-xs sm:text-sm text-gray-700 whitespace-pre-wrap">
                    {item.notes}
                  </p>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Footer with total */}
      {orderId && (
        <div className="flex items-center justify-between p-2 sm:p-3 bg-gray-50 border-t">
          <div className="font-medium text-sm sm:text-base">
            {t("total")}: {formatCurrency(totalPrice)}
          </div>
        </div>
      )}
    </div>
  );
}
